import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { usePartNumberActions } from 'pricing/hooks';
import { IPricingProfilePartNumber } from 'pricing/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  pricingProfilePartNumbers: IPricingProfilePartNumber[];
  buttonElement?: ReactElement;
  buttonLabel?: string;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
  notificationPosition?: NotificationPosition;
}

export const RemovePricingProfilePartNumbersButton = ({
  pricingProfilePartNumbers,
  buttonElement,
  buttonLabel,
  onRemoveSuccess,
  disabled = false,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { removePartNumbers } = usePartNumberActions({
    selectionCount: pricingProfilePartNumbers.length,
    position: notificationPosition,
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const removedPartNumbersIds = pricingProfilePartNumbers.map((partNumber) => partNumber.id);

    const { success } = await removePartNumbers({
      ids: removedPartNumbersIds,
    });

    if (!success) {
      return;
    }

    onRemoveSuccess?.();
  }, [onRemoveSuccess, pricingProfilePartNumbers, removePartNumbers]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, buttonLabel, disabled, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemovePricingProfilePartNumbers', {
          count: pricingProfilePartNumbers.length,
        })}
        message={t('messageConfirmRemovePricingProfilePartNumbers', {
          count: pricingProfilePartNumbers.length,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
